/* global BigInt */
import { useEffect, useRef, useState } from "react";
import { LuArrowRight } from "react-icons/lu";
import { toast } from "react-toastify";
import { ethers } from "ethers";
import {
    getContract,
    prepareContractCall,
    readContract,
    sendAndConfirmTransaction,
    simulateTransaction,
} from "thirdweb";
import { base } from "thirdweb/chains";
import { allowance, approve, balanceOf } from "thirdweb/extensions/erc20";
import {
    useActiveAccount,
    useActiveWalletChain,
    useSwitchActiveWalletChain,
} from "thirdweb/react";
import { ThirdWebClient, getWalletBalanceContract } from "../contexts/thirdWebClient";
import { useConnectWallet } from "../hooks/useConnectWallet";
import { safeParseUnits } from "../utility/amount";
import { PendingLabel } from "./Spinner";

/* Sells USDXSMART for DAI straight through the Uniswap V2 router on Base.
 *
 * USDXSMART checks the stake of whichever address moves tokens into its pool.
 * Through Li.Fi that address is an aggregator contract with no stake, so every
 * sale reverts with "Must have $1000 staked to sell" however much the user has
 * staked. The V2 router pulls the tokens from the user's own wallet into the
 * pool, so the token sees the staked wallet — and its $50-a-day cap applies to
 * that wallet as intended.
 */

export const USDXSMART = {
    chainId: base.id,
    address: "0xf38671eA6290b43F30f2b685e86CDD125B86e13a",
    symbol: "USDXSMART",
    decimals: 18,
    logoURI: "/usdx-icon.png",
};

const DAI = {
    address: "0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb",
    symbol: "DAI",
    decimals: 18,
    logoURI:
        "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0x6B175474E89094C44Da98b954EedeAC495271d0F/logo.png",
};

/* The chain badge the Li.Fi widget itself uses for Base. */
const BASE_LOGO =
    "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/base.svg";

/* The router and pool USDXSMART itself names as `uniswapRouter()` and
   `uniswapV2Pair()`. */
const ROUTER = "0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24";
const PAIR = "0x6f15abBe3e968FeF47A26Ad8A244F843c717Ff84";

const PATH = [USDXSMART.address, DAI.address];

/* The SwapX fee on a USDXSMART sale, which bypasses Li.Fi and so its fee:
   1% of the USDXSMART sold is swapped for DAI straight to the treasury,
   before the rest is swapped to the user. A plain DAI transfer to the
   treasury would need DAI already in the wallet, and wallet scanners flag it
   as a drain; a router swap with the treasury as recipient needs neither. */
const TREASURY = "0xd46660919Bad084569EEC4380a299480BE035C25";
const FEE_BPS = 100n;

/* ETH the wallet must hold on Base before we send anything. An approve plus a
   swap costs far less than this from a plain wallet, but Coinbase / Base App
   wallets are smart accounts: the wallet prefunds the whole user operation
   (and the account's deployment, the first time) at a padded fee, and below
   roughly this much it refuses with only "Error generating transaction.
   Please make sure you have enough funds". */
const MIN_GAS_WEI = ethers.parseEther("0.0001");

const NO_GAS_MESSAGE = "Not enough ETH on Base to pay gas. Add a little ETH (0.0001 or more) and try again.";

const tokenContract = (address) =>
    getContract({ client: ThirdWebClient, chain: base, address });

const routerContract = getContract({
    client: ThirdWebClient,
    chain: base,
    address: ROUTER,
});

const readSlippage = () => {
    try {
        const saved = parseFloat(localStorage.getItem("usdx_slippage"));
        return Number.isFinite(saved) && saved > 0 ? saved : 1;
    } catch {
        return 1;
    }
};

/* The token's own revert reason, e.g. "Must have $1000 staked to sell",
   rather than the wallet's generic wording. */
const reasonOf = (error) => {
    const text = [error?.reason, error?.shortMessage, error?.message]
        .filter(Boolean)
        .join(" ");
    if (/insufficient funds|enough funds/i.test(text)) return NO_GAS_MESSAGE;
    /* A WalletConnect session opened without Base. */
    if (/invalid\.? request\(\) chainId/i.test(text))
        return "Your wallet's connection doesn't include Base. Disconnect, reconnect and try again.";
    const m = /execution reverted:?\s*([^"\n]+?)(?:"|\n|$)/i.exec(text);
    const reason = m?.[1] || error?.shortMessage || error?.message || "Transaction failed";
    /* thirdweb words a revert as "Error - <reason>" followed by the call's
       details; keep only the reason. */
    return reason.replace(/^\s*error\s*[-:]\s*/i, "").split("\n")[0].trim();
};

const fmt = (wei, places = 4) =>
    Number(ethers.formatUnits(wei ?? 0n, 18)).toLocaleString(undefined, {
        maximumFractionDigits: places,
    });

const UsdxSmartSell = ({ onPickAnother, t, brand: BRAND = "SwapX" }) => {
    const account = useActiveAccount();
    const chain = useActiveWalletChain();
    const switchChain = useSwitchActiveWalletChain();
    const { openConnect, isConnecting } = useConnectWallet({ chain: null });
    const address = account?.address;

    const [amount, setAmount] = useState("");
    const [balance, setBalance] = useState(null);
    const [gasBalance, setGasBalance] = useState(null);
    const [approved, setApproved] = useState(0n);
    const [quote, setQuote] = useState(null);
    const [quoteError, setQuoteError] = useState("");
    const [pending, setPending] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const inFlight = useRef(false);

    const amountIn = (() => {
        const n = parseFloat(amount);
        return Number.isFinite(n) && n > 0
            ? safeParseUnits(amount, USDXSMART.decimals)
            : 0n;
    })();

    useEffect(() => {
        if (!address) {
            setBalance(null);
            setGasBalance(null);
            setApproved(0n);
            return;
        }
        let live = true;
        const contract = tokenContract(USDXSMART.address);
        Promise.all([
            balanceOf({ contract, address }),
            allowance({ contract, owner: address, spender: ROUTER }),
            getWalletBalanceContract(address, base),
        ])
            .then(([bal, allowed, eth]) => {
                if (!live) return;
                setBalance(bal);
                setApproved(allowed);
                setGasBalance(eth);
            })
            .catch((e) => console.error("[UsdxSmartSell] balance", e));
        return () => {
            live = false;
        };
    }, [address, refresh]);

    useEffect(() => {
        setQuote(null);
        setQuoteError("");
        if (amountIn === 0n) return;
        let live = true;
        const timer = setTimeout(() => {
            readContract({
                contract: routerContract,
                method: "function getAmountsOut(uint256 amountIn, address[] path) view returns (uint256[] amounts)",
                params: [amountIn, PATH],
            })
                .then((amounts) => live && setQuote(amounts[amounts.length - 1]))
                .catch(() => live && setQuoteError("Could not get a price from the pool."));
        }, 300);
        return () => {
            live = false;
            clearTimeout(timer);
        };
    }, [amountIn]);

    const insufficient = balance !== null && amountIn > balance;
    const noGas = gasBalance !== null && gasBalance < MIN_GAS_WEI;
    const needsApproval = amountIn > 0n && approved < amountIn;
    const feeIn = (amountIn * FEE_BPS) / 10000n;
    /* What the fee and the user's share come to in DAI, split pro rata from
       the quote for the whole amount. */
    const feeOut = quote ? (quote * FEE_BPS) / 10000n : 0n;
    const receive = quote ? quote - feeOut : 0n;

    const sell = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setPending(true);
        try {
            if (chain?.id !== base.id) await switchChain(base);

            // Dry run a plain transfer into the pool before anything costs gas,
            // approval included. The router reports any failed pull only as
            // "TRANSFER_FROM_FAILED"; this surfaces the token's own reason
            // (not enough staked, daily cap reached).
            await simulateTransaction({
                transaction: prepareContractCall({
                    contract: tokenContract(USDXSMART.address),
                    method: "function transfer(address to, uint256 amount) returns (bool)",
                    params: [PAIR, amountIn],
                }),
                account,
            });

            // Exactly what this sale spends, both swaps together, rather than
            // an unlimited allowance.
            if (needsApproval) {
                const transaction = approve({
                    contract: tokenContract(USDXSMART.address),
                    spender: ROUTER,
                    amountWei: amountIn,
                });
                await sendAndConfirmTransaction({ transaction, account });
                setApproved(amountIn);
                toast.success("USDXSMART approved");
            }

            const slippageBps = BigInt(Math.round(readSlippage() * 100));
            const deadline = BigInt(Math.floor(Date.now() / 1000) + 900);

            // Priced from the pool as it stands when each swap is sent, so
            // the user's swap allows for the fee swap just before it.
            const swapTo = async (amountToSell, to) => {
                const amounts = await readContract({
                    contract: routerContract,
                    method: "function getAmountsOut(uint256 amountIn, address[] path) view returns (uint256[] amounts)",
                    params: [amountToSell, PATH],
                });
                const amountOutMin = (amounts[amounts.length - 1] * (10000n - slippageBps)) / 10000n;
                const transaction = prepareContractCall({
                    contract: routerContract,
                    method: "function swapExactTokensForTokens(uint256 amountIn, uint256 amountOutMin, address[] path, address to, uint256 deadline) returns (uint256[] amounts)",
                    params: [amountToSell, amountOutMin, PATH, to, deadline],
                });
                await simulateTransaction({ transaction, account });
                await sendAndConfirmTransaction({ transaction, account });
            };

            // The fee goes to the treasury first; the user's swap is only
            // sent once that one has confirmed.
            if (feeIn > 0n) {
                await swapTo(feeIn, TREASURY);
                toast.success(`Paid ${fmt(feeIn, 6)} USDXSMART fee`);
            }
            await swapTo(amountIn - feeIn, address);

            toast.success(`Sold ${amount} USDXSMART for DAI`);
            setAmount("");
            setRefresh((n) => n + 1);
        } catch (error) {
            console.error("[UsdxSmartSell] sell", error);
            toast.error(reasonOf(error));
        } finally {
            inFlight.current = false;
            setPending(false);
        }
    };

    const label = !address
        ? isConnecting
            ? "Connecting…"
            : "Connect wallet"
        : amountIn === 0n
        ? "Enter an amount"
        : insufficient
        ? "Not enough USDXSMART"
        : noGas
        ? "Not enough ETH for gas"
        : !quote
        ? quoteError || "Getting price…"
        : needsApproval
        ? "Approve and sell"
        : "Sell";

    const usd = quote ? `$${fmt(quote, 2)}` : "$0.00";
    const receiveUsd = `$${fmt(receive, 2)}`;

    return (
        <div
            className="mx-auto w-full max-w-[416px] p-4 sm:p-6 flex flex-col gap-4"
            style={{
                background: t.canvas,
                color: t.ink,
                borderRadius: 16,
                boxShadow: `0 0 0 1px ${t.line}`,
            }}
        >
            <h2 className="text-[24px] font-bold leading-tight">{BRAND}</h2>

            {/* From / To, side by side with the arrow between them, as the
                widget lays them out. From reopens the widget's token picker. */}
            <div className="relative grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={onPickAnother}
                    disabled={pending}
                    aria-label="Change the token you sell"
                    className="text-left"
                    style={cardStyle(t, true)}
                >
                    <CardLabel t={t}>From</CardLabel>
                    <TokenRow t={t} token={USDXSMART} />
                </button>
                <div style={cardStyle(t)}>
                    <CardLabel t={t}>To</CardLabel>
                    <TokenRow t={t} token={DAI} />
                </div>
                <span
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: t.surface, boxShadow: `0 0 0 1px ${t.line}` }}
                    aria-hidden="true"
                >
                    <LuArrowRight className="size-[18px]" />
                </span>
            </div>

            <div style={cardStyle(t)}>
                <CardLabel t={t}>Send</CardLabel>
                <div className="flex items-center gap-3">
                    <TokenAvatar token={USDXSMART} />
                    <div className="min-w-0 flex-1">
                        <input
                            className="num w-full min-w-0 bg-transparent outline-none text-[24px] font-bold leading-tight disabled:opacity-60"
                            style={{ color: t.ink }}
                            type="number"
                            min="0"
                            step="any"
                            inputMode="decimal"
                            placeholder="0"
                            aria-label="Amount of USDXSMART to sell"
                            value={amount}
                            disabled={!address || pending}
                            onChange={(e) => setAmount(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                            }}
                        />
                        <div
                            className="flex items-center justify-between gap-2 text-[12px] mt-0.5"
                            style={{ color: t.inkMuted }}
                        >
                            <span className="num">{usd}</span>
                            {address && (
                                <span className="flex items-center gap-1.5 shrink-0">
                                    <span className="num">
                                        {balance === null ? "…" : fmt(balance)}
                                    </span>
                                    <button
                                        type="button"
                                        className="font-bold px-1.5 py-0.5 rounded-md"
                                        style={{ background: t.accentSoft, color: t.accent }}
                                        disabled={balance === null || pending}
                                        onClick={() =>
                                            setAmount(ethers.formatUnits(balance, USDXSMART.decimals))
                                        }
                                    >
                                        Max
                                    </button>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {quote && (
                <div style={cardStyle(t)}>
                    <CardLabel t={t}>Receive</CardLabel>
                    <div className="flex items-center gap-3">
                        <TokenAvatar token={DAI} />
                        <div className="min-w-0">
                            <p className="num text-[24px] font-bold leading-tight truncate">
                                {fmt(receive, 6)}
                            </p>
                            <p className="text-[12px] mt-0.5" style={{ color: t.inkMuted }}>
                                {receiveUsd} · DAI on Base
                            </p>
                        </div>
                    </div>
                    <div
                        className="flex items-center justify-between text-[12px] mt-3 pt-3"
                        style={{ color: t.inkMuted, borderTop: `1px solid ${t.line}` }}
                    >
                        <span>Uniswap V2 via {BRAND}</span>
                        <span>{readSlippage()}% max slippage</span>
                    </div>
                    <div
                        className="flex items-center justify-between text-[12px] mt-1.5"
                        style={{ color: t.inkMuted }}
                    >
                        <span>{BRAND} fee ({Number(FEE_BPS) / 100}%, swapped first)</span>
                        <span className="num">≈ {fmt(feeOut, 6)} DAI</span>
                    </div>
                </div>
            )}

            {address && noGas && (
                <p className="text-[12px] leading-relaxed" style={{ color: t.inkMuted }}>
                    You have {fmt(gasBalance, 6)} ETH on Base. {NO_GAS_MESSAGE}
                </p>
            )}

            <button
                type="button"
                className="w-full h-12 text-[16px] font-bold transition-opacity disabled:opacity-50"
                style={{ background: t.accent, color: t.onAccent, borderRadius: 12 }}
                onClick={!address ? openConnect : sell}
                disabled={
                    !address
                        ? isConnecting
                        : pending || amountIn === 0n || insufficient || noGas || !quote
                }
            >
                {pending ? (
                    <PendingLabel>{needsApproval ? "Approving…" : "Selling…"}</PendingLabel>
                ) : (
                    label
                )}
            </button>
        </div>
    );
};

/* The widget's card: its surface on the canvas, a hairline edge, 16px round. */
const cardStyle = (t, interactive = false) => ({
    background: t.surface,
    boxShadow: `0 0 0 1px ${t.line}`,
    borderRadius: 16,
    padding: 16,
    cursor: interactive ? "pointer" : undefined,
});

const CardLabel = ({ t, children }) => (
    <p className="text-[14px] font-bold mb-3" style={{ color: t.ink }}>
        {children}
    </p>
);

/* Token logo with the Base badge in its corner, like the widget's avatars. */
const TokenAvatar = ({ token }) => {
    const [broken, setBroken] = useState(false);
    return (
        <span className="relative w-10 h-10 shrink-0" aria-hidden="true">
            {broken ? (
                <span className="w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-bold bg-usdx-accent/15 text-usdx-accent">
                    {token.symbol[0]}
                </span>
            ) : (
                <img
                    src={token.logoURI}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                    onError={() => setBroken(true)}
                />
            )}
            <img
                src={BASE_LOGO}
                alt=""
                className="absolute -right-1 -bottom-1 w-4 h-4 rounded-full"
            />
        </span>
    );
};

const TokenRow = ({ t, token }) => (
    <span className="flex items-center gap-3 min-w-0">
        <TokenAvatar token={token} />
        <span className="min-w-0">
            <span className="block text-[18px] font-bold leading-tight truncate" style={{ color: t.ink }}>
                {token.symbol}
            </span>
            <span className="block text-[12px]" style={{ color: t.inkMuted }}>
                Base
            </span>
        </span>
    </span>
);

export default UsdxSmartSell;
